import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, CircularProgress, Box, Card, CardContent, Typography, Button } from '@mui/material';
import { AuthProvider, useAuth } from './hooks/useAuth';
import Login from './components/auth/Login';
import VenueAdminRegistration from './components/auth/VenueAdminRegistration';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import VenueAdminDashboard from './pages/VenueAdminDashboard';
import CreateVenue from './components/venue-admin/CreateVenue';
import CreateOffer from './components/venue-admin/CreateOffer';
import { glassTheme } from './theme/glassTheme';

// Protected Route component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { currentUser, userRole, loading } = useAuth();

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  if (!userRole || !allowedRoles.includes(userRole)) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
      >
        <h2>Access Denied</h2>
        <p>You don't have permission to access this area.</p>
        <p>Your role: {userRole || 'Unknown'}</p>
      </Box>
    );
  }

  return <>{children}</>;
};

// Pending Approval Component
const PendingApprovalPage = () => {
  const { signOut } = useAuth();

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      p={3}
    >
      <Card sx={{ maxWidth: 500, width: '100%' }}>
        <CardContent sx={{ textAlign: 'center', p: 4 }}>
          <Typography variant="h4" color="warning.main" gutterBottom>
            Account Pending Approval
          </Typography>
          <Typography variant="body1" paragraph>
            Your venue admin account is currently being reviewed by our team.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            You'll receive an email notification once your account is approved.
            This usually takes 1-2 business days.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Thank you for your patience!
          </Typography>
          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button 
              variant="outlined" 
              onClick={handleSignOut}
            >
              Sign Out
            </Button>
            <Button 
              variant="contained"
              onClick={() => window.location.reload()}
            >
              Check Status
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

// Unknown Role Component
const UnknownRolePage = () => {
  const { userRole, signOut } = useAuth();

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      p={3}
    >
      <Card sx={{ maxWidth: 500, width: '100%' }}>
        <CardContent sx={{ textAlign: 'center', p: 4 }}>
          <Typography variant="h4" gutterBottom>
            Welcome!
          </Typography>
          <Typography variant="body1" paragraph>
            Your account role: <strong>{userRole || 'Unknown'}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            This portal is for venue management. If you're a mobile app user, 
            please use the mobile app instead.
          </Typography>
          <Box sx={{ mt: 3 }}>
            <Button 
              variant="contained" 
              onClick={handleSignOut}
            >
              Sign Out
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

// Main App Router component
const AppRouter = () => {
  const { currentUser, userRole, loading } = useAuth();

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  // If not logged in, show login or registration
  if (!currentUser) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<VenueAdminRegistration />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
        {/* Remove this line - CreateVenue should only be accessible when logged in */}
      </Routes>
    );
  }

  // Redirect based on user role
  return (
    <Routes>
      {/* Login route - redirect logged in users */}
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/register" element={<Navigate to="/" replace />} />
      
      {/* Super Admin Routes */}
      <Route
        path="/super-admin"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <SuperAdminDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* Venue Admin Routes - CHANGE FROM WILDCARD TO SPECIFIC PATHS */}
      <Route
        path="/venue-admin"
        element={
          <ProtectedRoute allowedRoles={['venue_admin']}>
            <VenueAdminDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* ADD THE CREATE VENUE ROUTE HERE */}
      <Route
        path="/create-venue"
        element={
          <ProtectedRoute allowedRoles={['venue_admin']}>
            <CreateVenue />
          </ProtectedRoute>
        }
      />

      <Route
       path="/create-offer"
       element={
        <ProtectedRoute allowedRoles={['venue_admin']}>
          <CreateOffer />
          </ProtectedRoute>
       }
      />

      {/* Default redirect based on role */}
      <Route
        path="/"
        element={
          userRole === 'super_admin' ? (
            <Navigate to="/super-admin" replace />
          ) : userRole === 'venue_admin' ? (
            <Navigate to="/venue-admin" replace />
          ) : userRole === 'venue_admin_pending' ? (
            <PendingApprovalPage />
          ) : (
            <UnknownRolePage />
          )
        }
      />
      
      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Main App component
const App = () => {
  return (
    <ThemeProvider theme={glassTheme}>
      <CssBaseline />
      <Box sx={{
        background: '#f5f5f5',
        minHeight: '100vh',
      }}>
        <AuthProvider>
          <Router>
            <AppRouter />
          </Router>
        </AuthProvider>
      </Box>
    </ThemeProvider>
  );
};

export default App;